import { Auth } from "aws-amplify";
import { createContext, useEffect, useState } from "react";
import Cookies from 'js-cookie';
import { useLoginToggle } from "@/hooks";
import { Error } from "@/_service/notification/notification";
import { APP_ENV } from "@/_constant";
import { getUserDetails } from "@/apis";

const AuthContext = createContext();

function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isAuth, setAuth] = useState(false);
    const handleLoginToggle = useLoginToggle();
    
    const TOKEN_KEY = "token";

    const _setTokenInCookies = (session) => {
        const secureCookies = APP_ENV === 'prod';
        Cookies.set(TOKEN_KEY, session.getIdToken().getJwtToken(), { secure: secureCookies, sameSite: "Strict", path: "/" })
    }

    const fetchUser = async () => {
        try {
            const session = await Auth.currentSession();
            
            _setTokenInCookies(session);
            const { name, phone_number, phone_number_verified } = session.getIdToken().payload;
            const userDetailsResp = await getUserDetails();
            const userDetails = { ...userDetailsResp.response, name, phone_number, phone_number_verified };
            setUser(userDetails);
            setAuth(true);
            handleLoginToggle(false);
            return userDetails;
        } catch (err) {
            setUser(null);
            setAuth(false);
            // *** remove this line so that popup should always appears if and only if user is try to open protected routes ***
            // *** Show/Hide popup will be handled by 'ProtectedRoute' Component Because all the routes
            // handleLoginToggle(true); 
            Cookies.remove(TOKEN_KEY);
        }
    }

    const login = async (username, password) => {
        try{
            const user = await Auth.signIn(username, password);
            await fetchUser();
            return user;
        } catch(err){
            if (err.code === 'NotAuthorizedException') {
                Error(err.message);
            }
        }
    }

    const logout = async () => {
        await Auth.signOut();
        setUser(null);
        setAuth(false);
        Cookies.remove(TOKEN_KEY);
    }

    const signUp = (name, username, password, accessType) => {
        return new Promise(async (resolve, reject) => {
            try {
                const user = await Auth.signUp({
                    username: username,
                    password: password,
                    attributes: {
                        name: name,
                        'custom:access': accessType.toString(),
                    },
                });
                resolve(user);
            } catch (err) {
                if (err.code === 'UsernameExistsException') {
                    Error(`${username} already in use. Try Login`)
                } else if(err.code === 'TooManyRequestsException') {
                    Error("Too many Attempts. Please try after sometime")
                }
                reject(err);
            }
        });
    }

    const forgotPassword = (username) => {
        return new Promise(async (resolve, reject) => {
            try {
                const forgotPwdReq = await Auth.forgotPassword(username)
                resolve(forgotPwdReq);
            } catch (err) {
                if (err.code === "LimitExceededException") {
                    Error(err.message);
                }
                reject(err);
            }
        });
    }

    const forgotPasswordSubmit = (username, newPassword, otp) => {
        return new Promise((resolve, reject) => {
            try {
                resolve(Auth.forgotPasswordSubmit(username, otp, newPassword));
            } catch (err) {
                if (err.code === 'CodeMismatchException') {
                    Error("Invalid OTP");
                } else if(err.code === 'ExpiredCodeException'){
                    Error("Otp has been expired")
                }
                Error(err?.message);
                reject(err);
            }
        });
    }

    const refreshToken = () => {
        return new Promise((resolve, reject) => {
            try {
                const session = Auth.currentSession();
                console.info("Refresh token");
                resolve(session);
            } catch (err) {
                if (err.code === 'NotAuthorizedException') {
                    Error(err.message);
                }
                Cookies.remove(TOKEN_KEY);
                handleLoginToggle(true);
            }
        })
    }

    const verifyOTP = (username, otp) => {
        return new Promise((resolve, reject) => {
            try {

            } catch(err){
                if(err.code === 'CodeMismatchException') {
                    Error("Invalid Otp")
                } else if(err.code === 'ExpiredCodeException'){
                    Error("Otp has been expired")
                }
            }
        });
    }

    const sendOtp = (username) => {
        return new Promise((resolve, reject) => {

        });
    }

    const handleRefreshToken = () => {
        const refreshTokenInterval = setInterval(() => {
            refreshToken()
            .then(session=>{
                _setTokenInCookies(session);
            })
        }, 3599995); //3599995 ( less than 1hr)
    }

    const getIdToken = () => {
        return Cookies.get(TOKEN_KEY);
    }

    const updatePassword = (oldPassword, newPassword) => {
        return new Promise(async (resolve, reject) => {
            if(oldPassword === newPassword) {
                reject("Old and New Password cannot be same");
            }

            try {
                const user = await Auth.currentAuthenticatedUser();
                resolve(await Auth.changePassword(user, oldPassword, newPassword));
            } catch(err) {
                let message = "Failed to update password";
                switch (err.code) {
                    case 'NotAuthorizedException':
                        message = "The old password you entered is incorrect.";
                        break;
                    case 'InvalidPasswordException':
                        message = "New password does not meet complexity requirements.";
                        break;
                    case 'LimitExceededException':
                        message = "Too many attempts. Please try again later.";
                        break;
                    default:
                        message = "An unexpected error occurred:", error.message;
                }
                reject(message);
            }
        });
    }

    useEffect(() => {
        fetchUser()
        handleRefreshToken();
    }, []);

    return <AuthContext.Provider value={{ user, isAuth, login, logout, forgotPassword, forgotPasswordSubmit, signUp, getIdToken, updatePassword }}>{children}</AuthContext.Provider>
}

export default AuthContext;
export { AuthProvider };